import React, { JSX } from 'react'

import { cx } from '../../ui/utils'

/**
 * @param {number} count - how many of these skeletons should be rendered.
 * @param {number} height - either set height in parent or if height can be dynamic set an explicit 'placeholder' height here.
 * @param {boolean} isCircle - renders skeleton as a circle.
 * @param {boolean} shouldRemoveCurvedBorder - should curved borders be removed.
 *
 * Notes on width:
 * if parent of this component has display: flex, make sure to set parent to flex: 1 as well,
 * otherwise set parent width of how much you'd like skeleton to expand.
 
 * Example:
    <div aria-hidden className={styles['skeleton-parent-example']}>
      <Skeleton />    
    </div>
        
    ---
        
  .scss file:
    div.skeleton-parent-results {
      align-self: flex-start; 
      margin: 0.25rem 0 1.75rem;
      width: 15rem;
    }
 */
export interface SkeletonProps {
  count?: number
  height?: string
  isCircle?: boolean
  shouldRemoveCurvedBorder?: boolean
  className?: string
}

export default function Skeleton({
  height = '100%',
  count = 1,
  isCircle = false,
  shouldRemoveCurvedBorder = false,
  className,
}: SkeletonProps): JSX.Element[] {
  return Array.from({ length: count }).map((_, index) => (
    <div
      data-testid="skeleton"
      key={index}
      aria-hidden
      style={{ height }}
      className={cx(
        'skeleton ',
        isCircle ? 'rounded-full' : '',
        shouldRemoveCurvedBorder ? 'rounded-none' : '',
        className
      )}
    />
  ))
}
